import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ScopedConditionalContent from '@components/ScopedConditionalContent'
import Activities from '@components/Activities'
import useCreateOrEditOrderItemTaskModal from './hooks/useCreateOrEditOrderItemTaskModal'

import Attachments from './components/Attachments'
import Options from './components/Options'
import OrderArtwork from './components/OrderArtwork'
import OrderInstructions from './components/OrderInstructions'
import SelectOrderItem from './components/SelectOrderItem'
import TaskDetails from './components/TaskDetails'
import TaskType from './components/TaskType'
import TaskPrice from './components/TaskPrice'

const MODAL_KEY = 'CreateOrEditOrderItemTaskModal'

export type ModalCallbacks = {
  closeModal: () => void,
  completeOrderItemTask: (payload: HandleCallbackActionParams) => void,
  createOrderItemTask: (payload: HandleCallbackActionParams) => void,
  deleteOrderItemTask: (payload: HandleCallbackActionParams) => void,
  updateOrderItemTask: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  allowedTaskTypes: string[],
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditOrderItemTaskModal = (props: ModalProps) => {
  const { allowedTaskTypes, callbacks, showModal } = props
  const {
    closeModal,
    createOrderItemTask,
    updateOrderItemTask,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign, orderItemTask: initOrderItemTask } = modalPayload

  const hookPayload = useCreateOrEditOrderItemTaskModal(initOrderItemTask, { campaign })

  const {
    activitiesPayload,
    activitiesPayload: { hasFilteredActivities },
    callbacks: {
      createOrderItemTask: createFn,
      updateOrderItemTask: updateFn,
    },
    creating,
    disableEditing,
    orderItemTaskAttachmentsPayload,
    orderItemTaskAttachmentsPayload: { hasFilteredOrderItemTaskAttachments },
    formPayload,
    newRecord,
    orderItem,
    providerEmployeeUser,
    taskPrice,
    updating,
  } = hookPayload

  const {
    entityState,
    entityState: {
      due_date,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const createOrEditOrderItemTaskPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_order_item_campaign_id: true,
      include_task_instruction_image: true,
      include_task_order_item: true,
      include_task_provider_employee: true,
    },
    toastText: `Task ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Task" titleSecondLine="Details" />

      <SidebarModal.Content>
        <Form>
          {disableEditing && (
            <SidebarNoticeBox boxProps={{ marginBottom: 'large' }}>
              <SidebarNoticeBox.Title>Editing Disabled</SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                Please note you may only edit or delete a task up to two days before the task&apos;s due date and if
                your provider has enabled editing.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <SelectOrderItem hookPayload={hookPayload} />

          <TaskType allowedTaskTypes={allowedTaskTypes} hookPayload={hookPayload} />

          {!newRecord && <TaskDetails hookPayload={hookPayload} />}

          <Form.Field direction="column" errorMessage={errors.due_date} label="Due Date" marginTop="large">
            <input disabled={disableEditing} name="due_date" type="date" value={due_date} {...handlers} />
          </Form.Field>

          <ScopedConditionalContent
            permittedRoleTitles={['Provider Contractor', 'Provider Director', 'Provider Employee']}
          >
            <EntitySelector
              callbacks={{
                selectItem: user => setEntityState({ provider_employee_id: user ? user.actable_id : null }),
              }}
              disabled={disableEditing}
              entityKey="users"
              entityTitleKey="full_name"
              label="Assignee"
              marginTop="large"
              searchForwardProps={{ placeholder: 'Leave blank to auto assign' }}
              // @ts-expect-error TS2322
              selectedItemId={providerEmployeeUser?.id}
            />
          </ScopedConditionalContent>

          <OrderInstructions hookPayload={hookPayload} />

          <ScopedConditionalContent
            permittedRoleTitles={['Provider Contractor', 'Provider Director', 'Provider Employee']}
          >
            {orderItem?.artwork_id && !newRecord && (
              // @ts-expect-error TS2740
              <OrderArtwork orderItem={orderItem} />
            )}
          </ScopedConditionalContent>

          {hasFilteredOrderItemTaskAttachments && (
            <Attachments orderItemTaskAttachmentsPayload={orderItemTaskAttachmentsPayload} />
          )}

          {!newRecord && hasFilteredActivities && (
            <Activities activitiesPayload={activitiesPayload} />
          )}

          <TaskPrice taskPrice={taskPrice} />

          {!newRecord && <Options callbacks={callbacks} hookPayload={hookPayload} />}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={
            newRecord
              ? () => createOrderItemTask(createOrEditOrderItemTaskPayload)
              : () => updateOrderItemTask(createOrEditOrderItemTaskPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Task` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditOrderItemTaskModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
